/**
 * Feature flags — map feature keys to the minimum plan required.
 * Plan hierarchy: free (0) < pro/plus (1) < premium (2)
 *
 * DB stores 'free' | 'pro' | 'premium'.
 * UI shows "Plus" for 'pro' and "Premium" for 'premium'.
 *
 * To promote/demote a feature just change its plan value here.
 */
export const FEATURES = {
  // ── Free ────────────────────────────────────────────────────────────
  task_manual_add:      { plan: 'free',    label: 'Aggiunta manuale attività' },
  recurring_deadlines:  { plan: 'free',    label: 'Scadenze ricorrenti' },
  dashboard:            { plan: 'free',    label: 'Dashboard' },

  // ── Plus (DB: 'pro') ───────────────────────────────────────────────
  voice_input:          { plan: 'pro',     label: 'Aggiunta tramite microfono' },
  image_input:          { plan: 'pro',     label: 'Aggiunta tramite immagine' },
  unlimited_tasks:      { plan: 'pro',     label: 'Attività illimitate' },

  // ── Premium ─────────────────────────────────────────────────────────
  agent_chat:           { plan: 'premium', label: 'Mindesk Agent' },
  eisenhower_ai:        { plan: 'premium', label: 'Matrice Eisenhower AI' },
  pdf_to_practice:      { plan: 'premium', label: 'Pratiche da PDF (AI)' },
  traffic_alerts:       { plan: 'premium', label: 'Avviso traffico' },
}

/** Plan hierarchy for comparison */
const PLAN_LEVEL = { free: 0, pro: 1, premium: 2 }

/** Free users are limited to this many tasks */
export const FREE_TASK_LIMIT = 20

/**
 * Returns true if the user has currently-active paid access — either via a real
 * legacy Stripe subscription (recurring), or via an active one-time purchase / trial.
 *
 * Why the `stripe_subscription_id` check on the legacy branch:
 * the webhook for the one-time / trial flow used to set `subscription_status='active'`
 * permanently, which made trials never expire (gate stayed open via SUB_OK even after
 * `plan_expires_at` passed). Requiring `stripe_subscription_id` makes the legacy branch
 * fire only for true recurring subscriptions.
 */
export function hasActiveAccess(profile) {
  if (!profile) return false
  const legacySubActive = !!profile.stripe_subscription_id
    && (profile.subscription_status === 'active' || profile.subscription_status === 'trialing')
  const onetimeOk = profile.plan_expires_at && new Date(profile.plan_expires_at) > new Date()
  return legacySubActive || !!onetimeOk
}

/**
 * Computes the user's effective plan, accounting for both legacy subscription and
 * current annual one-time / trial models. Returns 'free' if access has lapsed.
 */
export function effectivePlan(profile) {
  if (!profile) return 'free'
  return hasActiveAccess(profile) ? (profile.plan || 'free') : 'free'
}

/**
 * Returns true if the user's plan unlocks the requested feature.
 * @param {string} featureKey - key from FEATURES
 * @param {string} userPlan   - 'free' | 'pro' | 'premium' (already-resolved effective plan)
 */
export function canUse(featureKey, userPlan = 'free') {
  const feature = FEATURES[featureKey]
  if (!feature) return true
  return (PLAN_LEVEL[userPlan] ?? 0) >= (PLAN_LEVEL[feature.plan] ?? 0)
}

/**
 * Returns the display name for a plan stored in DB.
 * DB 'pro' → UI 'Plus', DB 'premium' → UI 'Premium'.
 */
export function planDisplayName(dbPlan) {
  const names = { free: 'Free', pro: 'Plus', premium: 'Premium' }
  return names[dbPlan] || 'Free'
}

/**
 * React hook version — reads plan from authStore automatically.
 * Usage: const can = useFeature()
 *        if (can('voice_input')) { ... }
 */
import { useCallback } from 'react'
import useAuthStore from '../store/authStore'

export function useFeature() {
  const profile = useAuthStore(s => s.user)
  const plan = effectivePlan(profile)
  return useCallback((featureKey) => canUse(featureKey, plan), [plan])
}
